import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import PublicationDetail from './components/PublicationDetail';
import PublicationForm from './components/PublicationForm';
import UnifiedPublicationList from './components/UnifiedPublicationList';
import PublicPublicationDetail from './components/PublicPublicationDetail';
import HomePage from './components/HomePage';
import AboutUs from './components/AboutUs';
import ContactUs from './components/ContactUs';
import WeeklyHighlightsList from './components/WeeklyHighlightsList';
import WeeklyHighlightDetail from './components/WeeklyHighlightDetail';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';
import React from 'react';

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      
      {/* Publication Type Specific Routes */}
              <Route path="/research-papers" element={<UnifiedPublicationList isPublic={true} defaultFilter="Research Papers" />} />
        <Route path="/research-papers/:identifier" element={<PublicPublicationDetail />} />
        <Route path="/newsletters" element={<UnifiedPublicationList isPublic={true} defaultFilter="Newsletters" />} />
        <Route path="/newsletters/:identifier" element={<PublicPublicationDetail />} />
        <Route path="/blogs" element={<UnifiedPublicationList isPublic={true} defaultFilter="Blogs" />} />
        <Route path="/blogs/:identifier" element={<PublicPublicationDetail />} />
      
      {/* Legacy Publications Route - Keep for backward compatibility */}
      <Route path="/publications" element={<UnifiedPublicationList isPublic={true} />} />
      <Route path="/publications/:identifier" element={<PublicPublicationDetail />} />
      
      <Route path="/about" element={<AboutUs />} />
      <Route path="/contact" element={<ContactUs />} />
      
      {/* Legal Documents Routes - Public Access */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/:slug" element={<PrivacyPolicy />} />
      {/* Legacy routes for backward compatibility */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/terms-of-service" element={<TermsAndConditions />} />
      <Route path="/legal/:slug" element={<PrivacyPolicy />} />
      
      {/* Weekly Highlight Routes - Public Access */}
      <Route path="/weekly-highlights" element={<WeeklyHighlightsList />} />
      <Route path="/weekly-highlights/:identifier" element={<WeeklyHighlightDetail />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard/*" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      {/* Redirect any unknown routes to home page instead of dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
